import React, { useEffect, useState } from 'react';
import { Alert, Button } from '@mui/material';
import { useLicense } from '../context/LicenseContext';
import DatabaseService from '../services/database';
import { IS_WEB } from '../services/platform';
import { useLocation } from 'react-router-dom';

// Persistent status strip under the toolbar: trial countdown, payment
// problems, and read-only notice. Purely informational — enforcement happens
// in the main process.
const LicenseBanner = () => {
  const { state, openPrompt, recheck } = useLicense();
  const { pathname } = useLocation();
  const [birdCount, setBirdCount] = useState(null);

  const isTrial = state && state.mode === 'trial';
  // Re-count when the trial's own figures change (a license broadcast after an
  // add/PDF), not on every new state object identity — depending on `state`
  // itself would issue a redundant COUNT on every unrelated broadcast.
  const trialPdfUsed = state?.trial?.pdfUsed;

  useEffect(() => {
    let cancelled = false;
    if (isTrial) {
      DatabaseService.getBirdCount()
        .then((count) => { if (!cancelled) setBirdCount(count); })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, [isTrial, trialPdfUsed, pathname]);

  if (!state || state.devBypass) return null;

  if (state.mode === 'unlicensed') {
    return (
      <Alert severity="info" sx={{ borderRadius: 0, flexWrap: 'wrap', '& .MuiAlert-action': { ml: 0 } }}
        action={<Button color="inherit" onClick={openPrompt} sx={{ minHeight: 44 }}>Start free trial</Button>}>
        Your account is ready. Start your free 7-day trial to create pedigrees. No card required.
      </Alert>
    );
  }

  if (state.mode === 'trial') {
    const daysLeft = state.expiresAt
      ? Math.max(0, Math.ceil((state.expiresAt - Date.now()) / (24 * 60 * 60 * 1000)))
      : null;
    const bits = [];
    if (daysLeft !== null) bits.push(`${daysLeft} day${daysLeft === 1 ? '' : 's'} left`);
    if (birdCount !== null && state.trial) bits.push(`${Math.min(birdCount, state.trial.maxBirds)}/${state.trial.maxBirds} birds`);
    if (state.trial) bits.push(`${Math.min(state.trial.pdfUsed, state.trial.maxPdf)}/${state.trial.maxPdf} PDF downloads`);
    return (
      <Alert
        severity="info"
        sx={{ borderRadius: 0 }}
        action={<Button color="inherit" size="small" onClick={openPrompt}>Upgrade</Button>}
      >
        Free trial — {bits.join(' · ')}
      </Alert>
    );
  }

  if (state.mode === 'past_due') {
    return (
      <Alert
        severity="warning"
        sx={{ borderRadius: 0 }}
        action={<Button color="inherit" size="small" onClick={() => recheck()}>I've fixed it — check now</Button>}
      >
        There's a problem with your subscription payment. Please check your card — the app keeps working during the grace period.
      </Alert>
    );
  }

  if (state.mode === 'readonly') {
    return (
      <Alert
        severity="error"
        sx={{ borderRadius: 0 }}
        action={<Button color="inherit" size="small" onClick={openPrompt}>Reactivate</Button>}
      >
        Read-only mode — your birds are safe and can be viewed or exported, but adding and editing requires {IS_WEB ? 'an active paid plan' : 'an active license'}.
      </Alert>
    );
  }

  return null;
};

export default LicenseBanner;
